import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useMyEngagementQuery } from '@paintscout/api'
import { registerSurveyTrigger } from '@paintscout/util'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { isE2E } from 'shared/util/e2e'
import { usePageViewItem } from 'shared/components/engagement/usePageViewItem'
import { isSurfaceOpen, setSurfaceOpen, useSurfaceOpen } from '../engagementSurfaces'
import SurveyDialog from '../../dialogs/SurveyDialog/SurveyDialog'
import { pickAutoShow, readSessionShown } from '../AnnouncementCenter/announcementQueue'
import type { Survey } from './surveyQueue'
import {
  findManualSurvey,
  isAnnouncementSettled,
  isPageViewSurveyCandidate,
  pickOnLoginSurvey,
  readSessionSurveyShown,
  writeSessionSurveyShown
} from './surveyQueue'

/**
 * In-house surveys (#25349), behind `inHouseSurveys`; renders nothing during BugBug runs.
 *
 * - Registers the handler `triggerSurvey(key)` calls, so the app and the packages open manual
 *   surveys without reading the flag. With the flag off nothing is registered and every trigger
 *   runs its Userflow flow.
 * - Opens the newest unanswered on-login survey by itself, once per tab session, after the
 *   announcement AnnouncementCenter opened by itself (if any) is closed.
 * - Opens a page-view survey (#25610) when the current page matches its path pattern, it is
 *   neither answered nor dismissed, and no announcement or other survey is open.
 */
export default function SurveyCenter() {
  const { inHouseSurveys, announcementCenter } = useFlags()
  const [e2e] = useState(isE2E)
  const enabled = !!inHouseSurveys && !e2e
  const { enqueueSnackbar } = useSnackbar()

  // cache-first: AuthorizeUser fires myEngagement at startup, and AnnouncementCenter reads the same.
  const { data } = useMyEngagementQuery({ fetchPolicy: 'cache-first', skip: !enabled })
  const engagement = data?.myEngagement
  const surveys = useMemo(() => engagement?.surveys ?? [], [engagement])

  const [current, setCurrentState] = useState<Survey | null>(null)
  const surveysRef = useRef(surveys)
  surveysRef.current = surveys
  // Set in the same call that opens the dialog, so AnnouncementCenter's effect in this commit sees it.
  const setCurrent = useCallback((survey: Survey | null) => {
    setSurfaceOpen('survey', !!survey)
    setCurrentState(survey)
  }, [])
  useEffect(() => () => setSurfaceOpen('survey', false), [])

  const announcementOpen = useSurfaceOpen('announcement')
  // Page-view surveys opened in this tab, in case the dismissed or answered write fails.
  const pageShownRef = useRef(new Set<string>())
  const pageViewNext = usePageViewItem(enabled ? surveys : [], (s) =>
    isPageViewSurveyCandidate(s, pageShownRef.current)
  )

  // Read in render, before AnnouncementCenter's effect records the one it opens on this load.
  const announcementShownAtMountRef = useRef(readSessionShown())
  const surveyShownRef = useRef(readSessionSurveyShown())
  // The announcement AnnouncementCenter opens by itself on this load, once known.
  const pendingAnnouncementRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    return registerSurveyTrigger((key) => {
      const survey = findManualSurvey(surveysRef.current, key)
      if (!survey) return false
      if (survey.answeredAt) {
        enqueueSnackbar('You have already answered this survey. Thanks!', { variant: 'info' })
      } else {
        setCurrent(survey)
      }
      return true
    })
  }, [enabled, enqueueSnackbar, setCurrent])

  useEffect(() => {
    if (!enabled || !engagement || current || surveyShownRef.current) return
    // A page-view announcement can be open too (#25610); `announcementOpen` re-runs this on close.
    if (announcementOpen || isSurfaceOpen('announcement')) return
    const announcements = engagement.announcements ?? []
    // Looked up on every result until one is found, so a later fetch or a late flag still counts.
    // Then kept: once AnnouncementCenter marks it viewed, pickAutoShow no longer returns it.
    if (!pendingAnnouncementRef.current && announcementCenter) {
      pendingAnnouncementRef.current = pickAutoShow(announcements, announcementShownAtMountRef.current)?.id ?? null
    }
    if (!isAnnouncementSettled(announcements, pendingAnnouncementRef.current)) return
    const next = pickOnLoginSurvey(engagement.surveys ?? [], surveyShownRef.current)
    if (!next) return
    surveyShownRef.current = true
    writeSessionSurveyShown(next.id)
    setCurrent(next)
  }, [enabled, engagement, current, announcementCenter, announcementOpen, setCurrent])

  useEffect(() => {
    // The store, not only the state: AnnouncementCenter's effect runs first in the same commit.
    if (!enabled || !pageViewNext || current || announcementOpen || isSurfaceOpen('announcement')) return
    pageShownRef.current.add(pageViewNext.id)
    setCurrent(pageViewNext)
  }, [enabled, pageViewNext, current, announcementOpen, setCurrent])

  if (!enabled || !current) return null
  return <SurveyDialog key={current.id} survey={current} onClose={() => setCurrent(null)} />
}
